import { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Zap, Pencil, Check, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Compact banner for Super Admin showing current-month LLM key usage.
 * - Renders only when state is "warning" or "exceeded" (otherwise stays out of the way).
 * - Click the pencil to edit the monthly call threshold inline.
 */
export default function LLMBudgetBanner() {
  const [usage, setUsage] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/super-admin/llm-usage`, { headers });
      setUsage(res.data);
      setDraft(String(res.data.threshold));
    } catch (e) {
      // Silent — banner stays hidden if it fails to load.
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(id);
  }, []);

  const save = async () => {
    const n = parseInt(draft, 10);
    if (Number.isNaN(n) || n < 0) {
      toast.error('Threshold must be a non-negative integer');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/super-admin/llm-usage/threshold`,
        { threshold: n },
        { headers }
      );
      setUsage(res.data);
      setEditing(false);
      toast.success('LLM budget threshold updated');
    } catch (e) {
      toast.error('Failed to update threshold');
    } finally {
      setSaving(false);
    }
  };

  if (!usage) return null;

  const isExceeded = usage.state === 'exceeded';
  const isWarning = usage.state === 'warning';

  // OK state: collapsed mini-pill (still informative for super admin)
  if (!isExceeded && !isWarning) {
    return (
      <div
        data-testid="llm-budget-banner-ok"
        className="mb-6 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800"
      >
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4" />
          <span className="font-medium">LLM budget</span>
          <span className="text-emerald-700">
            {usage.calls_this_month.toLocaleString()} / {usage.threshold.toLocaleString()} calls this month
            ({usage.pct_used}%)
          </span>
        </div>
        {!editing ? (
          <button
            data-testid="llm-budget-edit-btn"
            onClick={() => setEditing(true)}
            className="text-xs text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
          >
            <Pencil className="w-3 h-3" /> Edit threshold
          </button>
        ) : (
          <ThresholdEditor
            draft={draft}
            setDraft={setDraft}
            onSave={save}
            onCancel={() => { setEditing(false); setDraft(String(usage.threshold)); }}
            saving={saving}
          />
        )}
      </div>
    );
  }

  // Warning / exceeded: full attention-grabbing banner
  const palette = isExceeded
    ? 'border-red-300 bg-red-50 text-red-900'
    : 'border-amber-300 bg-amber-50 text-amber-900';
  const iconColor = isExceeded ? 'text-red-600' : 'text-amber-600';

  return (
    <div
      data-testid={isExceeded ? 'llm-budget-banner-exceeded' : 'llm-budget-banner-warning'}
      className={`mb-6 rounded-lg border ${palette} px-4 py-3`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className={`w-5 h-5 mt-0.5 ${iconColor}`} />
          <div>
            <p className="font-semibold">
              {isExceeded ? 'LLM budget exceeded' : 'LLM budget warning'}
            </p>
            <p className="text-sm opacity-90">
              {usage.calls_this_month.toLocaleString()} of {usage.threshold.toLocaleString()} monthly LLM calls used
              {' '}({usage.pct_used}%). Top up your Emergent Universal Key or raise the threshold below.
            </p>
          </div>
        </div>
        {!editing ? (
          <Button
            data-testid="llm-budget-edit-btn"
            size="sm"
            variant="outline"
            onClick={() => setEditing(true)}
          >
            <Pencil className="w-3 h-3 mr-1" /> Adjust threshold
          </Button>
        ) : (
          <ThresholdEditor
            draft={draft}
            setDraft={setDraft}
            onSave={save}
            onCancel={() => { setEditing(false); setDraft(String(usage.threshold)); }}
            saving={saving}
          />
        )}
      </div>
    </div>
  );
}

function ThresholdEditor({ draft, setDraft, onSave, onCancel, saving }) {
  return (
    <div className="flex items-center gap-2">
      <Input
        data-testid="llm-budget-threshold-input"
        type="number"
        min="0"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="h-8 w-28"
      />
      <Button
        data-testid="llm-budget-save-btn"
        size="sm"
        onClick={onSave}
        disabled={saving}
      >
        <Check className="w-3 h-3" />
      </Button>
      <Button
        data-testid="llm-budget-cancel-btn"
        size="sm"
        variant="ghost"
        onClick={onCancel}
        disabled={saving}
      >
        <X className="w-3 h-3" />
      </Button>
    </div>
  );
}
